/**
 * Tile Registry - Factory system for creating tile instances
 *
 * Provides a centralized factory function that maps TILE constants
 * to their corresponding tile class instances.
 */

import { TILE } from "../constants/gameConstants";
import { TileIndex } from "../types";
import { BaseTile } from "./BaseTile";

// Import all tile classes
import { ArrowTile } from "./ArrowTile";
import { BallEntity } from "./BallEntity";
import { BeeEntity } from "./BeeEntity";
import { BombTile } from "./BombTile";
import { BugEntity } from "./BugEntity";
import { ButtonDoorTile } from "./ButtonDoorTile";
import { ButtonTile } from "./ButtonTile";
import { CatEntity } from "./CatEntity";
import { DoorTile } from "./DoorTile";
import { EmptyTile } from "./EmptyTile";
import { ExitTile } from "./ExitTile";
import { FlippersTile } from "./FlippersTile";
import { HintTile } from "./HintTile";
import { KeyTile } from "./KeyTile";
import { PlayerEntity } from "./PlayerEntity";
import { RotationButtonTile } from "./RotationButtonTile";
import { SandTile } from "./SandTile";
import { StarTile } from "./StarTile";
import { SuctionBootsTile } from "./SuctionBootsTile";
import { WallTile } from "./WallTile";
import { WaterBlockEntity } from "./WaterBlockEntity";
import { WaterTile } from "./WaterTile";

/**
 * Factory function to create tile instances from level data.
 * Handles parameterized tiles (keys, doors, arrows, bees).
 *
 * @param tileType - The TILE constant from level data
 * @param col - Column position
 * @param row - Row position
 * @returns BaseTile instance
 * @throws Error if tile type is unknown
 */
export function createTileInstance(
  tileType: TileIndex,
  col: number,
  row: number
): BaseTile {
  switch (tileType) {
    case TILE.EMPTY:
      return new EmptyTile({ col, row });

    case TILE.WALL:
      return new WallTile({ col, row });

    case TILE.STAR:
      return new StarTile({ col, row });

    case TILE.BOMB:
      return new BombTile({ col, row });

    // Parameterized: Keys
    case TILE.KEY_GREEN:
      return new KeyTile({ col, row, color: "green" });
    case TILE.KEY_RED:
      return new KeyTile({ col, row, color: "red" });
    case TILE.KEY_ORANGE:
      return new KeyTile({ col, row, color: "orange" });
    case TILE.KEY_BLUE:
      return new KeyTile({ col, row, color: "blue" });

    // Parameterized: Doors
    case TILE.DOOR_GREEN:
      return new DoorTile({ col, row, color: "green" });
    case TILE.DOOR_RED:
      return new DoorTile({ col, row, color: "red" });
    case TILE.DOOR_ORANGE:
      return new DoorTile({ col, row, color: "orange" });
    case TILE.DOOR_BLUE:
      return new DoorTile({ col, row, color: "blue" });

    case TILE.PLAYER_START:
      return new PlayerEntity({ col, row });

    case TILE.SAND:
      return new SandTile({ col, row });

    case TILE.BUG:
      return new BugEntity({ col, row });

    case TILE.EXIT:
      return new ExitTile({ col, row });

    case TILE.WATER:
      return new WaterTile({ col, row });

    case TILE.FLIPPERS:
      return new FlippersTile({ col, row });

    case TILE.WATER_BLOCK:
      return new WaterBlockEntity({ col, row });

    case TILE.HINT:
      return new HintTile({ col, row });

    // Parameterized: Arrows
    case TILE.ARROW_UP:
      return new ArrowTile({ col, row, direction: "up" });
    case TILE.ARROW_DOWN:
      return new ArrowTile({ col, row, direction: "down" });
    case TILE.ARROW_LEFT:
      return new ArrowTile({ col, row, direction: "left" });
    case TILE.ARROW_RIGHT:
      return new ArrowTile({ col, row, direction: "right" });

    case TILE.SUCTION_BOOTS:
      return new SuctionBootsTile({ col, row });

    case TILE.BUTTON:
      return new ButtonTile({ col, row });

    case TILE.BUTTON_DOOR:
      return new ButtonDoorTile({ col, row });

    // Parameterized: Bees
    case TILE.BEE_HORIZONTAL:
      return new BeeEntity({ col, row, orientation: "horizontal" });
    case TILE.BEE_VERTICAL:
      return new BeeEntity({ col, row, orientation: "vertical" });

    case TILE.CAT:
      return new CatEntity({ col, row });

    case TILE.BALL:
      return new BallEntity({ col, row });

    case TILE.ROTATION_BUTTON:
      return new RotationButtonTile({ col, row });

    case TILE.BUTTON_DOOR_OPEN:
      return new ButtonDoorTile({ col, row, startOpen: true });

    default:
      throw new Error(`Unknown tile type: ${tileType}`);
  }
}

/**
 * Validate that all TILE constants have factory support
 * Call this during initialization to ensure completeness
 */
export function validateTileRegistry(): void {
  const allTileTypes = Object.values(TILE);
  const missingTypes: number[] = [];

  for (const tileType of allTileTypes) {
    const instance = createTileInstance(tileType, 0, 0);
    if (!instance) {
      missingTypes.push(tileType);
    }
  }

  if (missingTypes.length > 0) {
    throw new Error(
      `Missing factory cases for TILE types: ${missingTypes.join(", ")}`
    );
  }

  console.log(`✅ Tile registry validated: All ${allTileTypes.length} tile types supported`);
}
